import { ScannersList } from '@/components/hotel-access-code-scans';
import { AccessCodeDetailsCard } from '@/components/hotel-access-codes/AccessCodeDetailsCard';
import type { HotelAccessCode } from '@/config/objects/hotel-access-codes';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  StatusPill,
  toast,
  useApiClient,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Single-page detail view for a hotel access code.
 *
 * Replaces the previous tabbed layout with one scrolling page: a details card,
 * the QR code card, and the guests (scanners) list at the bottom. Reachable both
 * from the hotel portal (`/hotels/:id/access-codes/:accessCodeId`) and the
 * internal top-level list (`/hotel-access-codes/:id`).
 */
export function HotelAccessCodeDetailPage() {
  // Top-level route uses `:id`; the hotel-nested route uses `:accessCodeId`.
  const { id, accessCodeId } = useParams<{ id?: string; accessCodeId?: string }>();
  const codeId = accessCodeId ?? id;
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { isInternal } = useRequireOrganization();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: HotelAccessCode }>({
    queryKey: ['HotelAccessCode', codeId],
    queryFn: () => apiClient.get(`/hotel-access-codes/${codeId}`),
    enabled: !!codeId,
  });
  const accessCode = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (): Promise<{ success: boolean; error?: string }> =>
      apiClient.delete(`/hotel-access-codes/${codeId}`),
    onSuccess: (response) => {
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete access code');
      }
      queryClient.invalidateQueries({ queryKey: ['HotelAccessCode'] });
      navigate('/hotel-access-codes');
    },
  });

  const handleDelete = () => {
    toast.promise(deleteMutation.mutateAsync(), {
      loading: 'Deleting access code...',
      success: 'Access code deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete access code',
    });
  };

  const breadcrumbs: Array<{ label: string; href?: string; current?: boolean }> = [
    { label: 'Home', href: '/' },
    { label: 'Hotels', href: '/hotels' },
    ...(accessCode?.hotel
      ? [{ label: accessCode.hotel.name, href: `/hotels/${accessCode.hotel.id}` }]
      : []),
    { label: 'Access Codes', href: '/hotel-access-codes' },
    { label: accessCode?.name || 'Access Code', current: true },
  ];

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          accessCode && isInternal ? (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/hotel-access-codes/${codeId}/edit`)}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : undefined
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Access Code', icon: KeyRound }}
        title={accessCode?.name || 'Access Code'}
        image={{
          url: null,
          alt: accessCode?.name || 'Access Code',
          fallbackIcon: KeyRound,
        }}
        meta={
          accessCode?.hotel?.name ? <span>{accessCode.hotel.name}</span> : undefined
        }
        badges={
          accessCode ? (
            <>
              <StatusPill
                status={accessCode.isActive ? 'published' : 'needs_review'}
                label={accessCode.isActive ? 'Active' : 'Inactive'}
              />
              {accessCode.revokedAt && <StatusPill status="needs_review" label="Revoked" />}
            </>
          ) : undefined
        }
      >
        {accessCode && (
          <div className="space-y-6">
            <AccessCodeDetailsCard accessCode={accessCode} />

            <ScannersList
              accessCodeId={codeId}
              hotelId={accessCode.hotelId}
              entityName="Guest"
              entityNamePlural="Guests"
            />
          </div>
        )}
      </DetailPageHero>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Access Code"
        description={`Are you sure you want to delete "${accessCode?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
